import { RootState } from '@AppState/redux-store'
import { PushNotificationServiceAPI } from '@API/data-services/push-notification-service'
import { PushNotificationCategorySource } from '@Generated/back-office-client/src'
import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  Switch,
  Table,
  Text,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSelector } from 'react-redux'

const KEY_REGEX = /^[A-Z][A-Z0-9_]*$/

export const PushNotificationCategories = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()

  const [key, setKey] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [defaultEnabled, setDefaultEnabled] = useState(true)

  const queryKey = ['push-notification-categories', activeCustomerId]

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => PushNotificationServiceAPI.listCategories(activeCustomerId!),
    enabled: !!activeCustomerId,
  })

  const {
    mutate: register,
    isPending: saving,
    error: saveError,
  } = useMutation({
    mutationFn: () =>
      PushNotificationServiceAPI.registerCategory(activeCustomerId!, {
        key,
        displayName,
        defaultEnabled,
      }),
    onSuccess: () => {
      setKey('')
      setDisplayName('')
      setDefaultEnabled(true)
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const errorMessage = (fetchError: Error | null, saveErr: Error | null): string | null => {
    if (fetchError) {
      return fetchError.message
    }
    if (saveErr) {
      return saveErr.message
    }
    return null
  }

  const keyValid = key === '' || (KEY_REGEX.test(key) && key.length <= 64)
  const formValid = key.length > 0 && keyValid && displayName.length > 0
  const errMsg = errorMessage(error, saveError)

  return (
    <Container>
      <Flex direction="column" gap="4">
        <Heading>Push Notification Categories</Heading>

        <Card>
          <Flex direction="column" gap="3">
            <Heading size="4">Existing Categories</Heading>
            {isLoading ? (
              <Text>Loading...</Text>
            ) : (
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Key</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Display Name</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Default Enabled</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {data?.categories.map((cat) => (
                    <Table.Row key={cat.key}>
                      <Table.Cell>{cat.key}</Table.Cell>
                      <Table.Cell>{cat.displayName}</Table.Cell>
                      <Table.Cell>{cat.defaultEnabled ? 'Yes' : 'No'}</Table.Cell>
                      <Table.Cell>
                        <Badge
                          color={
                            cat.source === PushNotificationCategorySource.BUILT_IN ? 'gray' : 'blue'
                          }>
                          {cat.source === PushNotificationCategorySource.BUILT_IN
                            ? 'Built-in'
                            : 'Custom'}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="3">
            <Heading size="4">Register New Custom Category</Heading>

            <Box>
              <Text as="label" size="2" weight="medium">
                Key
              </Text>
              <Text as="p" size="1" color="gray" mb="1">
                Uppercase letters, digits, and underscores only. Must start with a letter. Max 64
                characters.
              </Text>
              <TextField.Root
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="PROMO_FRIDAY_DEALS"
                color={keyValid ? undefined : 'red'}
              />
            </Box>

            <Box>
              <Text as="label" size="2" weight="medium">
                Display Name
              </Text>
              <TextField.Root
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Friday Deals"
              />
            </Box>

            <Flex align="center" gap="2">
              <Switch checked={defaultEnabled} onCheckedChange={setDefaultEnabled} />
              <Text as="label" size="2" weight="medium">
                Default enabled for new players
              </Text>
            </Flex>

            {errMsg && (
              <Text color="red" size="2">
                {errMsg}
              </Text>
            )}

            <Flex>
              <Button onClick={() => register()} disabled={!formValid || saving} loading={saving}>
                Register Category
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Flex>
    </Container>
  )
}
